import { useState, useEffect } from 'react';
import {
  User, Mail, Phone, MapPin, Calendar, Shield, Smartphone, Laptop, Globe,
  LogOut, Clock, Camera, Save, Loader2, CheckCircle, AlertCircle,
  Eye, EyeOff, History, Settings as SettingsIcon, ChevronRight,
  Package, CheckCircle2, AlertTriangle, XCircle, Receipt, Download,
  X as CloseIcon, CreditCard, Hash
} from 'lucide-react';
import { authController } from '../../controllers/authController';
import paymentController from '../../controllers/paymentController';

function Spinner(props) {
  var size = props.size || 16;
  return (
    <Loader2 size={size} style={{ display: 'inline', animation: 'spin 1s linear infinite' }} />
  );
}

function Toast(props) {
  var type = props.type;
  var message = props.message;
  var onClose = props.onClose;
  var colors = type === 'error'
    ? { bg: '#fef2f2', border: '#fca5a5', color: '#b91c1c' }
    : { bg: '#f0fdf4', border: '#86efac', color: '#15803d' };
  var borderStyle = '1px solid ' + colors.border;
  return (
    <div style={{ background: colors.bg, border: borderStyle, borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: colors.color, fontSize: 13 }}>
      {type === 'error' ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.color, padding: 0 }}>×</button>
    </div>
  );
}

/* ═══════════════════════════ PROFILE INFO TAB ═══════════════════════════ */
function ProfileInfoTab(props) {
  var user = props.user;
  var onUpdated = props.onUpdated;
  var notify = props.notify;

  var initialForm = {
    firstName: (user && user.firstName) || '',
    lastName: (user && user.lastName) || '',
    phone: (user && user.phone) || '',
    bio: (user && user.bio) || '',
    gender: (user && user.gender) || '',
    dateOfBirth: (user && user.dateOfBirth) ? user.dateOfBirth.split('T')[0] : '',
    street: (user && user.address && user.address.street) || '',
    city: (user && user.address && user.address.city) || '',
    state: (user && user.address && user.address.state) || '',
    zipCode: (user && user.address && user.address.zipCode) || '',
    country: (user && user.address && user.address.country) || ''
  };

  var formState = useState(initialForm);
  var form = formState[0];
  var setForm = formState[1];

  var avatarFileState = useState(null);
  var avatarFile = avatarFileState[0];
  var setAvatarFile = avatarFileState[1];

  var avatarPreviewState = useState((user && user.avatar) || null);
  var avatarPreview = avatarPreviewState[0];
  var setAvatarPreview = avatarPreviewState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  function handleChange(e) {
    var name = e.target.name;
    var value = e.target.value;
    setForm(function (p) {
      var next = {};
      for (var k in p) { next[k] = p[k]; }
      next[name] = value;
      return next;
    });
  }

  function handleAvatarChange(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);

    var fd = new FormData();
    fd.append('firstName', form.firstName);
    fd.append('lastName', form.lastName);
    if (form.phone) fd.append('phone', form.phone);
    if (form.bio) fd.append('bio', form.bio);
    if (form.gender) fd.append('gender', form.gender);
    if (form.dateOfBirth) fd.append('dateOfBirth', form.dateOfBirth);
    fd.append('address', JSON.stringify({
      street: form.street,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      country: form.country
    }));
    if (avatarFile) fd.append('avatar', avatarFile);

    authController.updateProfile(fd).then(function (res) {
      setSaving(false);
      if (res.success) {
        notify('success', 'Profile updated successfully');
        onUpdated(res.data.user);
      } else {
        notify('error', res.message || 'Update failed');
      }
    });
  }

  var s = {
    avatarRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
    avatarWrap: { position: 'relative', width: 84, height: 84, flexShrink: 0 },
    avatar: { width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', background: '#e2e8f0' },
    avatarPlaceholder: { width: 84, height: 84, borderRadius: '50%', background: '#3b82f6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700 },
    cameraBtn: { position: 'absolute', bottom: 0, right: 0, background: '#1e293b', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid #fff' },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
    group: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
    label: { fontSize: 12, fontWeight: 600, color: '#475569' },
    input: { padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
    textarea: { padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box', height: 60, resize: 'vertical' },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '20px 0 10px', textTransform: 'uppercase', letterSpacing: '0.4px' },
    saveBtn: { marginTop: 10, padding: '11px 22px', background: saving ? '#94a3b8' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }
  };

  var initials = ((form.firstName && form.firstName[0]) || '') + ((form.lastName && form.lastName[0]) || '');
  initials = initials.toUpperCase();

  return (
    <form onSubmit={handleSubmit}>
      <div style={s.avatarRow}>
        <div style={s.avatarWrap}>
          {avatarPreview ? (
            <img src={avatarPreview} alt="avatar" style={s.avatar} />
          ) : (
            <div style={s.avatarPlaceholder}>{initials ? initials : <User size={32} />}</div>
          )}
          <label style={s.cameraBtn}>
            <Camera size={13} />
            <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
          </label>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{form.firstName} {form.lastName}</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>{user ? user.email : ''}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, textTransform: 'capitalize' }}>{user && user.role ? user.role.replace('_', ' ') : ''}</div>
        </div>
      </div>

      <div style={s.grid2}>
        <div style={s.group}>
          <label style={s.label}>First Name</label>
          <input style={s.input} name="firstName" value={form.firstName} onChange={handleChange} />
        </div>
        <div style={s.group}>
          <label style={s.label}>Last Name</label>
          <input style={s.input} name="lastName" value={form.lastName} onChange={handleChange} />
        </div>
      </div>

      <div style={s.grid2}>
        <div style={s.group}>
          <label style={s.label}>Phone</label>
          <input style={s.input} name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit number" />
        </div>
        <div style={s.group}>
          <label style={s.label}>Date of Birth</label>
          <input style={s.input} type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} />
        </div>
      </div>

      <div style={s.group}>
        <label style={s.label}>Gender</label>
        <select style={s.input} name="gender" value={form.gender} onChange={handleChange}>
          <option value="">Select</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
          <option value="prefer_not_to_say">Prefer not to say</option>
        </select>
      </div>

      <div style={s.group}>
        <label style={s.label}>Bio</label>
        <textarea style={s.textarea} name="bio" value={form.bio} onChange={handleChange} maxLength={500} />
      </div>

      <div style={s.sectionTitle}>Address</div>
      <div style={s.group}>
        <label style={s.label}>Street</label>
        <input style={s.input} name="street" value={form.street} onChange={handleChange} />
      </div>
      <div style={s.grid2}>
        <div style={s.group}>
          <label style={s.label}>City</label>
          <input style={s.input} name="city" value={form.city} onChange={handleChange} />
        </div>
        <div style={s.group}>
          <label style={s.label}>State</label>
          <input style={s.input} name="state" value={form.state} onChange={handleChange} />
        </div>
      </div>
      <div style={s.grid2}>
        <div style={s.group}>
          <label style={s.label}>Zip Code</label>
          <input style={s.input} name="zipCode" value={form.zipCode} onChange={handleChange} />
        </div>
        <div style={s.group}>
          <label style={s.label}>Country</label>
          <input style={s.input} name="country" value={form.country} onChange={handleChange} />
        </div>
      </div>

      <button type="submit" disabled={saving} style={s.saveBtn}>
        {saving ? (
          <span><Spinner size={14} /> Saving...</span>
        ) : (
          <span><Save size={14} /> Save Changes</span>
        )}
      </button>
    </form>
  );
}

/* ═══════════════════════════ CHANGE PASSWORD TAB ═══════════════════════════ */
function ChangePasswordTab(props) {
  var notify = props.notify;

  var formState = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  var form = formState[0];
  var setForm = formState[1];

  var showState = useState({ current: false, next: false, confirm: false });
  var show = showState[0];
  var setShow = showState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  function handleSubmit(e) {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      notify('error', 'New passwords do not match');
      return;
    }
    if (form.newPassword.length < 8) {
      notify('error', 'Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    authController.changePassword(form.currentPassword, form.newPassword).then(function (res) {
      setSaving(false);
      if (res.success) {
        notify('success', 'Password changed successfully');
        setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        notify('error', res.message || 'Failed to change password');
      }
    });
  }

  var s = {
    group: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, maxWidth: 380 },
    label: { fontSize: 12, fontWeight: 600, color: '#475569' },
    pwWrap: { position: 'relative' },
    input: { padding: '10px 40px 10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
    eyeBtn: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' },
    saveBtn: { marginTop: 6, padding: '11px 22px', background: saving ? '#94a3b8' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }
  };

  var fieldMap = { current: 'currentPassword', next: 'newPassword', confirm: 'confirmPassword' };
  var labelMap = { current: 'Current Password', next: 'New Password', confirm: 'Confirm New Password' };
  var keys = ['current', 'next', 'confirm'];

  return (
    <form onSubmit={handleSubmit}>
      {keys.map(function (key) {
        var fieldName = fieldMap[key];
        return (
          <div style={s.group} key={key}>
            <label style={s.label}>{labelMap[key]}</label>
            <div style={s.pwWrap}>
              <input
                style={s.input}
                type={show[key] ? 'text' : 'password'}
                value={form[fieldName]}
                onChange={function (e) {
                  var val = e.target.value;
                  setForm(function (p) {
                    var next = {};
                    for (var k in p) { next[k] = p[k]; }
                    next[fieldName] = val;
                    return next;
                  });
                }}
                required
              />
              <button
                type="button"
                style={s.eyeBtn}
                onClick={function () {
                  setShow(function (p) {
                    var next = {};
                    for (var k in p) { next[k] = p[k]; }
                    next[key] = !p[key];
                    return next;
                  });
                }}
              >
                {show[key] ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        );
      })}
      <button type="submit" disabled={saving} style={s.saveBtn}>
        {saving ? (
          <span><Spinner size={14} /> Updating...</span>
        ) : (
          <span><Save size={14} /> Update Password</span>
        )}
      </button>
    </form>
  );
}

/* ═══════════════════════════ DEVICE SESSIONS TAB ═══════════════════════════ */
function DeviceSessionsTab(props) {
  var notify = props.notify;

  var sessionsState = useState([]);
  var sessions = sessionsState[0];
  var setSessions = sessionsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var loggingOutState = useState(null);
  var loggingOut = loggingOutState[0];
  var setLoggingOut = loggingOutState[1];

  function load() {
    setLoading(true);
    authController.getSessions().then(function (res) {
      if (res.success) setSessions(res.data.sessions || []);
      setLoading(false);
    });
  }

  useEffect(function () {
    load();
  }, []);

  function handleLogout(sessionId) {
    setLoggingOut(sessionId);
    authController.logoutSession(sessionId).then(function (res) {
      if (res.success) {
        setSessions(function (prev) {
          return prev.filter(function (s) { return s.sessionId !== sessionId; });
        });
        notify('success', 'Device logged out');
      } else {
        notify('error', res.message || 'Failed to logout device');
      }
      setLoggingOut(null);
    });
  }

  function getIcon(deviceType) {
    if (deviceType === 'android' || deviceType === 'ios') return <Smartphone size={18} />;
    if (deviceType === 'web') return <Globe size={18} />;
    return <Laptop size={18} />;
  }

  var s = {
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 10, background: '#fff' },
    left: { display: 'flex', alignItems: 'center', gap: 12 },
    iconWrap: { width: 40, height: 40, borderRadius: '50%', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    name: { fontSize: 14, fontWeight: 600, color: '#1e293b' },
    meta: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
    currentBadge: { fontSize: 11, fontWeight: 700, color: '#059669', background: '#d1fae5', padding: '2px 8px', borderRadius: 999, marginLeft: 8 },
    logoutBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid #fca5a5', color: '#dc2626', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    empty: { textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }
  };

  if (loading) {
    return <div style={s.empty}><Spinner size={20} /></div>;
  }

  return (
    <div>
      {sessions.length === 0 ? <div style={s.empty}>Koi active device nahi mila</div> : null}
      {sessions.map(function (sess) {
        return (
          <div key={sess.sessionId} style={s.row}>
            <div style={s.left}>
              <div style={s.iconWrap}>{getIcon(sess.deviceType)}</div>
              <div>
                <div style={s.name}>
                  {sess.deviceName}
                  {sess.isCurrent ? <span style={s.currentBadge}>This device</span> : null}
                </div>
                <div style={s.meta}>
                  {sess.deviceType} - Logged in {new Date(sess.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
            {!sess.isCurrent ? (
              <button style={s.logoutBtn} onClick={function () { handleLogout(sess.sessionId); }} disabled={loggingOut === sess.sessionId}>
                <LogOut size={13} />
                {loggingOut === sess.sessionId ? 'Logging out...' : 'Logout'}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════ LOGIN HISTORY TAB ═══════════════════════════ */
function LoginHistoryTab() {
  var historyState = useState([]);
  var history = historyState[0];
  var setHistory = historyState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  useEffect(function () {
    authController.getLoginHistory().then(function (res) {
      if (res.success) setHistory(res.data.loginHistory || []);
      setLoading(false);
    });
  }, []);

  var s = {
    row: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
    iconWrap: { width: 34, height: 34, borderRadius: '50%', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#64748b' },
    date: { fontSize: 13, fontWeight: 600, color: '#1e293b' },
    meta: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
    methodBadge: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 6, marginLeft: 8, background: '#eff6ff', color: '#2563eb' },
    empty: { textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }
  };

  if (loading) {
    return <div style={s.empty}><Spinner size={20} /></div>;
  }

  return (
    <div>
      {history.length === 0 ? <div style={s.empty}>Koi login history nahi mili</div> : null}
      {history.map(function (h, i) {
        return (
          <div key={i} style={s.row}>
            <div style={s.iconWrap}><Clock size={16} /></div>
            <div style={{ flex: 1 }}>
              <div style={s.date}>
                {new Date(h.loginAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {h.loginMethod ? <span style={s.methodBadge}>{h.loginMethod}</span> : null}
              </div>
              <div style={s.meta}>
                {h.deviceType || 'unknown device'} {h.ipAddress ? ('- ' + h.ipAddress) : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════ INVOICE DETAILS MODAL ═══════════════════════════ */
var INVOICE_STATUS_META = {
  paid: { label: 'Paid', color: '#16a34a', bg: '#dcfce7' },
  sent: { label: 'Pending', color: '#d97706', bg: '#fef3c7' },
  cancelled: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2' },
  refunded: { label: 'Refunded', color: '#64748b', bg: '#f1f5f9' },
  partially_paid: { label: 'Partial', color: '#d97706', bg: '#fef3c7' },
  overdue: { label: 'Overdue', color: '#dc2626', bg: '#fef2f2' },
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9' }
};

function InvoiceDetailsModal(props) {
  var invoice = props.invoice;
  var onClose = props.onClose;

  // NOTE: All hooks are declared here, at the top, BEFORE any early return.
  // This is required by the Rules of Hooks — hooks must run in the same
  // order on every render, so they can never sit after a conditional
  // `return`. This is what was causing the "Hooks must be called in the
  // exact same order" error.
  var downloadingState = useState(false);
  var downloading = downloadingState[0];
  var setDownloading = downloadingState[1];

  if (!invoice) return null;

  var meta = INVOICE_STATUS_META[invoice.status] || INVOICE_STATUS_META.draft;

  var s = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 16 },
    card: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', position: 'relative' },
    header: { padding: '20px 22px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    close: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' },
    invNumber: { fontSize: 16, fontWeight: 700, color: '#1e293b' },
    statusBadge: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: meta.color, background: meta.bg, marginTop: 6 },
    body: { padding: '18px 22px' },
    row: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #f8fafc' },
    iconWrap: { color: '#94a3b8', flexShrink: 0, marginTop: 1 },
    rowLabel: { fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' },
    rowValue: { fontSize: 13, fontWeight: 600, color: '#1e293b', marginTop: 2 },
    rowValueCap: { fontSize: 13, fontWeight: 600, color: '#1e293b', marginTop: 2, textTransform: 'capitalize' },
    rowValueMono: { fontSize: 12, fontWeight: 600, color: '#1e293b', marginTop: 2, fontFamily: 'monospace' },
    amountBox: { background: '#f8fafc', borderRadius: 10, padding: 16, marginTop: 14, textAlign: 'center' },
    amountLabel: { fontSize: 11, color: '#94a3b8', textTransform: 'uppercase' },
    amountValue: { fontSize: 28, fontWeight: 700, color: '#1e293b', marginTop: 4 },
    footer: { padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8 },
    downloadBtn: { flex: 1, padding: '11px', borderRadius: 8, background: downloading ? '#94a3b8' : '#2563eb', color: '#fff', border: 'none', fontWeight: 600, fontSize: 13, cursor: downloading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
    disabledBtn: { flex: 1, padding: '11px', borderRadius: 8, background: '#e2e8f0', color: '#94a3b8', border: 'none', fontWeight: 600, fontSize: 13, textAlign: 'center' }
  };

  var subDetails = invoice.subscriptionDetails || {};
  var startDateStr = subDetails.startDate ? new Date(subDetails.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  var endDateStr = subDetails.endDate ? new Date(subDetails.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  var validPeriodStr = startDateStr + ' to ' + endDateStr;
  var includedSections = subDetails.includedSections || [];

  function stopPropagation(e) {
    e.stopPropagation();
  }

  function handleDownload() {
    setDownloading(true);
    paymentController.downloadInvoice(invoice._id, invoice.invoiceNumber).then(function (res) {
      setDownloading(false);
      // res.success false ho to chahe to yahan error dikha sakte ho
    });
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.card} onClick={stopPropagation}>
        <div style={s.header}>
          <div>
            <div style={s.invNumber}>{invoice.invoiceNumber}</div>
            <span style={s.statusBadge}>{meta.label}</span>
          </div>
          <button style={s.close} onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        <div style={s.body}>
          <div style={s.row}>
            <Package size={16} style={s.iconWrap} />
            <div>
              <div style={s.rowLabel}>Plan</div>
              <div style={s.rowValue}>{subDetails.planName || 'Subscription'}</div>
            </div>
          </div>

          <div style={s.row}>
            <Calendar size={16} style={s.iconWrap} />
            <div>
              <div style={s.rowLabel}>Billing Cycle</div>
              <div style={s.rowValueCap}>{subDetails.billingCycle || '-'}</div>
            </div>
          </div>

          <div style={s.row}>
            <Calendar size={16} style={s.iconWrap} />
            <div>
              <div style={s.rowLabel}>Valid Period</div>
              <div style={s.rowValue}>{validPeriodStr}</div>
            </div>
          </div>

          {includedSections.length > 1 ? (
            <div style={s.row}>
              <Package size={16} style={s.iconWrap} />
              <div>
                <div style={s.rowLabel}>Included Modules</div>
                <div style={s.rowValue}>{includedSections.join(', ')}</div>
              </div>
            </div>
          ) : null}

          <div style={s.row}>
            <CreditCard size={16} style={s.iconWrap} />
            <div>
              <div style={s.rowLabel}>Payment Method</div>
              <div style={s.rowValueCap}>{invoice.paymentGateway || '-'}</div>
            </div>
          </div>

          {invoice.gatewayPaymentId ? (
            <div style={s.row}>
              <Hash size={16} style={s.iconWrap} />
              <div>
                <div style={s.rowLabel}>Payment Reference</div>
                <div style={s.rowValueMono}>{invoice.gatewayPaymentId}</div>
              </div>
            </div>
          ) : null}

          <div style={s.row}>
            <Calendar size={16} style={s.iconWrap} />
            <div>
              <div style={s.rowLabel}>{invoice.status === 'paid' ? 'Paid On' : 'Issued On'}</div>
              <div style={s.rowValue}>
                {new Date(invoice.paidDate || invoice.issueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>

          {subDetails.isRenewal ? (
            <div style={s.row}>
              <Hash size={16} style={s.iconWrap} />
              <div>
                <div style={s.rowLabel}>Type</div>
                <div style={s.rowValue}>Renewal</div>
              </div>
            </div>
          ) : null}

          <div style={s.amountBox}>
            <div style={s.amountLabel}>Total Amount</div>
            <div style={s.amountValue}>Rs. {invoice.totalAmount}</div>
          </div>
        </div>

        <div style={s.footer}>
          {invoice.status === 'paid' ? (
            <button style={s.downloadBtn} onClick={handleDownload} disabled={downloading}>
              {downloading ? <Spinner size={15} /> : <Download size={15} />}
              {downloading ? 'Downloading...' : 'Download Invoice PDF'}
            </button>
          ) : (
            <div style={s.disabledBtn}>Invoice download available after payment is confirmed</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ MY SUBSCRIPTIONS TAB ═══════════════════════════ */
var SUB_STATUS_META = {
  active: { label: 'Active', color: '#16a34a', bg: '#dcfce7', Icon: CheckCircle2 },
  expiring_soon: { label: 'Expiring Soon', color: '#d97706', bg: '#fef3c7', Icon: AlertTriangle },
  expired: { label: 'Expired', color: '#dc2626', bg: '#fef2f2', Icon: XCircle }
};

function MySubscriptionsTab() {
  var subsState = useState([]);
  var subs = subsState[0];
  var setSubs = subsState[1];

  var invoicesState = useState([]);
  var invoices = invoicesState[0];
  var setInvoices = invoicesState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var viewState = useState('active');
  var view = viewState[0];
  var setView = viewState[1];

  var selectedInvoiceState = useState(null);
  var selectedInvoice = selectedInvoiceState[0];
  var setSelectedInvoice = selectedInvoiceState[1];

  useEffect(function () {
    setLoading(true);
    Promise.all([
      authController.getMySubscriptions(),
      paymentController.getMyInvoices()
    ]).then(function (results) {
      var subsRes = results[0];
      var invRes = results[1];
      setSubs(subsRes.success ? (subsRes.data.subscriptions || []) : []);
      setInvoices(invRes.success ? invRes.data : []);
      setLoading(false);
    });
  }, []);

  function findInvoiceForSub(sub) {
    var found = null;
    for (var i = 0; i < invoices.length; i++) {
      var inv = invoices[i];
      if (inv.subscriptionDetails && inv.subscriptionDetails.planId === sub.planId && inv.status === 'paid') {
        found = inv;
        break;
      }
    }
    return found;
  }

  function activeCount() {
    var count = 0;
    for (var i = 0; i < subs.length; i++) {
      if (subs[i].status !== 'expired') count++;
    }
    return count;
  }

  var s = {
    toggle: { display: 'flex', gap: 4, marginBottom: 18, background: '#f8fafc', padding: 4, borderRadius: 9, width: 'fit-content' },
    card: { border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' },
    planName: { fontSize: 14, fontWeight: 700, color: '#1e293b', textTransform: 'capitalize' },
    meta: { fontSize: 12, color: '#94a3b8', marginTop: 4 },
    daysLeft: { fontSize: 11, color: '#64748b', marginTop: 6 },
    empty: { textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 },
    invRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
    invLeft: { display: 'flex', alignItems: 'center', gap: 12 },
    invIconWrap: { width: 34, height: 34, borderRadius: 8, background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    invNumber: { fontSize: 13, fontWeight: 700, color: '#1e293b' },
    invMeta: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    invAmount: { fontSize: 14, fontWeight: 700, color: '#1e293b' },
    downloadBtn: { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b', marginLeft: 10, textDecoration: 'none' },
    hint: { fontSize: 10, color: '#cbd5e1', marginTop: 4 }
  };

  function toggleBtnStyle(active) {
    return {
      padding: '7px 16px',
      background: active ? '#fff' : 'transparent',
      color: active ? '#1e293b' : '#64748b',
      border: 'none',
      borderRadius: 7,
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 600,
      boxShadow: active ? '0 1px 3px rgba(0,0,0,.1)' : 'none'
    };
  }

  function statusBadgeStyle(color, bg) {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      color: color,
      background: bg
    };
  }

  if (loading) {
    return <div style={s.empty}><Spinner size={20} /></div>;
  }

  function handleActiveClick(sub) {
    var inv = findInvoiceForSub(sub);
    if (inv) setSelectedInvoice(inv);
  }

  function handleHistoryClick(inv) {
    setSelectedInvoice(inv);
  }

  function closeModal() {
    setSelectedInvoice(null);
  }

  return (
    <div>
      <div style={s.toggle}>
        <button style={toggleBtnStyle(view === 'active')} onClick={function () { setView('active'); }}>
          Active Subscriptions ({activeCount()})
        </button>
        <button style={toggleBtnStyle(view === 'history')} onClick={function () { setView('history'); }}>
          Purchase History ({invoices.length})
        </button>
      </div>

      {view === 'active' ? (
        subs.length === 0 ? (
          <div style={s.empty}>
            <Package size={32} color="#e2e8f0" style={{ marginBottom: 10 }} />
            <div>Koi active subscription nahi hai</div>
          </div>
        ) : (
          subs.map(function (sub, i) {
            var meta = SUB_STATUS_META[sub.status] || SUB_STATUS_META.active;
            var Icon = meta.Icon;
            var planLabel = sub.planId ? sub.planId.replace(/-/g, ' ') : '';
            var expiryLine = null;
            if (sub.expiresAt) {
              var expDateStr = new Date(sub.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
              expiryLine = sub.status === 'expired'
                ? ('Expired on ' + expDateStr)
                : ('Expires on ' + expDateStr + ' (' + sub.daysLeft + ' din baaki)');
            }
            return (
              <div key={i} style={s.card} onClick={function () { handleActiveClick(sub); }}>
                <div>
                  <div style={s.planName}>{planLabel}</div>
                  <div style={s.meta}>
                    Billing: {sub.billingCycle} - Activated {new Date(sub.activatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  {expiryLine ? <div style={s.daysLeft}>{expiryLine}</div> : null}
                  <div style={s.hint}>Details ke liye click karein</div>
                </div>
                <span style={statusBadgeStyle(meta.color, meta.bg)}>
                  <Icon size={12} /> {meta.label}
                </span>
              </div>
            );
          })
        )
      ) : null}

      {view === 'history' ? (
        invoices.length === 0 ? (
          <div style={s.empty}>
            <Receipt size={32} color="#e2e8f0" style={{ marginBottom: 10 }} />
            <div>Koi purchase history nahi hai</div>
          </div>
        ) : (
          invoices.map(function (inv) {
            var meta = INVOICE_STATUS_META[inv.status] || INVOICE_STATUS_META.draft;
            var planName = (inv.subscriptionDetails && inv.subscriptionDetails.planName) || 'Subscription';

            function onDownloadClick(e) {
              e.stopPropagation();
              paymentController.downloadInvoice(inv._id, inv.invoiceNumber);
            }

            return (
              <div key={inv._id} style={s.invRow} onClick={function () { handleHistoryClick(inv); }}>
                <div style={s.invLeft}>
                  <div style={s.invIconWrap}><Receipt size={16} /></div>
                  <div>
                    <div style={s.invNumber}>{inv.invoiceNumber}</div>
                    <div style={s.invMeta}>
                      {planName} - {new Date(inv.issueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={s.invAmount}>Rs. {inv.totalAmount}</div>
                    <span style={statusBadgeStyle(meta.color, meta.bg)}>{meta.label}</span>
                  </div>
                  {inv.status === 'paid' ? (
                    <button
                      style={s.downloadBtn}
                      title="Download invoice"
                      onClick={onDownloadClick}
                    >
                      <Download size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )
      ) : null}

      {selectedInvoice ? (
        <InvoiceDetailsModal invoice={selectedInvoice} onClose={closeModal} />
      ) : null}
    </div>
  );
}

/* ═══════════════════════════ MAIN PROFILE SETTINGS PAGE ═══════════════════════════ */
function ProfileSettings() {
  var userState = useState(authController.getCurrentUser());
  var user = userState[0];
  var setUser = userState[1];

  var activeTabState = useState('profile');
  var activeTab = activeTabState[0];
  var setActiveTab = activeTabState[1];

  var toastState = useState(null);
  var toast = toastState[0];
  var setToast = toastState[1];

  function notify(type, message) {
    setToast({ type: type, message: message });
    setTimeout(function () { setToast(null); }, 4000);
  }

  var tabs = [
    { id: 'profile', label: 'Profile Info', icon: User },
    { id: 'subscriptions', label: 'My Subscriptions', icon: Package },
    { id: 'security', label: 'Change Password', icon: Shield },
    { id: 'sessions', label: 'Active Devices', icon: Smartphone },
    { id: 'history', label: 'Login History', icon: History }
  ];

  var s = {
    page: { padding: 24, background: '#f8fafc', minHeight: '100vh' },
    header: { marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 700, color: '#1e293b', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 },
    sub: { fontSize: 13, color: '#64748b', margin: 0 },
    layout: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, maxWidth: 900 },
    sidebar: { background: '#fff', borderRadius: 12, padding: 8, height: 'fit-content', boxShadow: '0 1px 3px rgba(0,0,0,.06)' },
    content: { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }
  };

  function navItemStyle(active) {
    return {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: 600,
      color: active ? '#2563eb' : '#475569',
      background: active ? '#eff6ff' : 'transparent',
      marginBottom: 2
    };
  }

  var spinKeyframes = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';

  return (
    <div style={s.page}>
      <style>{spinKeyframes}</style>

      <div style={s.header}>
        <h1 style={s.title}><SettingsIcon size={22} color="#3b82f6" /> Profile Settings</h1>
        <p style={s.sub}>Apna profile, password, subscriptions, aur active devices manage karein</p>
      </div>

      <div style={s.layout}>
        <div style={s.sidebar}>
          {tabs.map(function (tab) {
            var Icon = tab.icon;
            return (
              <div key={tab.id} style={navItemStyle(activeTab === tab.id)} onClick={function () { setActiveTab(tab.id); }}>
                <Icon size={16} />
                <span style={{ flex: 1 }}>{tab.label}</span>
                {activeTab === tab.id ? <ChevronRight size={14} /> : null}
              </div>
            );
          })}
        </div>

        <div style={s.content}>
          {toast ? <Toast type={toast.type} message={toast.message} onClose={function () { setToast(null); }} /> : null}

          {activeTab === 'profile' ? <ProfileInfoTab user={user} onUpdated={setUser} notify={notify} /> : null}
          {activeTab === 'subscriptions' ? <MySubscriptionsTab /> : null}
          {activeTab === 'security' ? <ChangePasswordTab notify={notify} /> : null}
          {activeTab === 'sessions' ? <DeviceSessionsTab notify={notify} /> : null}
          {activeTab === 'history' ? <LoginHistoryTab /> : null}
        </div>
      </div>
    </div>
  );
}

export default ProfileSettings;